import { ApplicableSubscriptionsReadOnly, User } from 'hotdesk_typescript';
import {
    getActiveUserSubscriptions,
    getCoveredSubscriptionName,
    getPreferredUserSubscriptionIds,
    pickOldestActiveUserSubscription
} from '@/hotdesk-ui/utils/subscriptionUtils';
import { MyListing } from '@/hotdesk-ui/services/listing.service';

/** API often returns arrays; generated types use singular objects. */
export const toSubscriptionArray = <T>(value: T | T[] | null | undefined): T[] => {
    if (value == null) return [];
    return Array.isArray(value) ? value : [value];
};

/** Maps listing type id to promo key used in package.package.promos (legacy PriceClient). */
export const getListingTypePromoKey = (listingType?: number): string => {
    switch (listingType) {
        case 1:
            return 'desk';
        case 2:
            return 'meetingRoom';
        case 3:
            return 'officeRoom';
        default:
            return 'desk';
    }
};

const getDirectPromoFieldKey = (listingType?: number): string | null => {
    switch (listingType) {
        case 1:
            return 'hotdeskPromo';
        case 2:
            return 'meetingRoomPromo';
        case 3:
            return 'officePromo';
        default:
            return null;
    }
};

export const getListingPromoType = (
    listingType?: number
): 'hotdeskPromo' | 'meetingRoomPromo' | null => {
    switch (listingType) {
        case 1:
            return 'hotdeskPromo';
        case 2:
            return 'meetingRoomPromo';
        default:
            return null;
    }
};

export type SubscriptionPromoDetails = {
    appliesFor: string[];
    cap: number | null;
    packageName: string | null;
};

type RawRecord = Record<string, unknown>;

const extractPromoFromUserSub = (
    userSub: RawRecord,
    listingType?: number
): { appliesFor: string[]; cap: number | null } => {
    const promoKey = getListingTypePromoKey(listingType);
    const packageNode = userSub.package as RawRecord | undefined;
    const packageInner = packageNode?.package as RawRecord | undefined;
    const promos = packageInner?.promos as Record<string, RawRecord> | undefined;
    const packagePromo = promos?.[promoKey];

    if (packagePromo?.appliesFor && Array.isArray(packagePromo.appliesFor)) {
        const appliesFor = packagePromo.appliesFor as string[];
        if (appliesFor.length > 0) {
            return {
                appliesFor,
                cap:
                    typeof packagePromo.cap === 'number'
                        ? packagePromo.cap
                        : null,
            };
        }
    }

    const directKey = getDirectPromoFieldKey(listingType);
    if (directKey) {
        const direct = userSub[directKey] as RawRecord | undefined;
        if (direct) {
            const extraRules = direct.extraRules as RawRecord | undefined;
            const appliesFor = (extraRules?.appliesFor ??
                direct.appliesFor) as string[] | undefined;
            let cap: number | null = null;
            if (direct.maxCap != null && direct.maxCap !== '') {
                const parsed = Number(direct.maxCap);
                cap = Number.isFinite(parsed) ? parsed : null;
            } else if (typeof direct.value === 'number') {
                cap = direct.value;
            }
            if (appliesFor?.length || cap != null) {
                return { appliesFor: appliesFor ?? [], cap };
            }
        }
    }

    return { appliesFor: [], cap: null };
};

/** True when this user-subscription package has a promo for the listing type. */
export const userSubscriptionCoversListingType = (
    userSub: RawRecord | null | undefined,
    listingType?: number
): boolean => {
    if (!userSub) return false;
    if (listingType == null) return true;
    return extractPromoFromUserSub(userSub, listingType).appliesFor.length > 0;
};

/**
 * Listing-card "Applied" label: the membership must cover this listing type
 * (desk / meeting room / office), not merely a sibling listing in the same
 * workspace. Details uses the same promo check, so the badge and banner agree.
 */
export const getListingCardSubscriptionName = (
    user: User | null,
    listing?: MyListing | null,
    coveredWorkspaceIds: number[] = [],
    coveredListingIds: number[] = [],
    activeSubscriptionIdIn?: string | string[] | null
): string | null => {
    const name = getCoveredSubscriptionName(
        user,
        'listing',
        listing,
        coveredWorkspaceIds,
        coveredListingIds,
        activeSubscriptionIdIn
    );
    if (!name || !listing) return name;

    const preferredIds = getPreferredUserSubscriptionIds(activeSubscriptionIdIn);
    const applicable = listing.metaData?.applicableSubscriptions as
        | number[]
        | undefined;
    const picked = pickOldestActiveUserSubscription(
        getActiveUserSubscriptions(user),
        {
            applicableIds: applicable,
            preferredIds,
            requireApplicableMatch: !!applicable?.length
        }
    );

    if (
        !userSubscriptionCoversListingType(
            picked as RawRecord | undefined,
            listing.type
        )
    ) {
        return null;
    }

    return name;
};

/**
 * Prefer the oldest active user subscription covering this listing so card,
 * details, and booking all apply the same package when several are active.
 *
 * When `preferredIds` is set (explore filtered to specific subscription(s)),
 * those win over the global oldest so details match the card the user clicked.
 * `fallbackSubscriptions` fills in preferred rows missing from the applicable
 * payload (some BE responses only return a single covering sub).
 */
/** User subscriptions applicable to this listing (API payload + profile fallbacks). */
export const getListingApplicableUserSubscriptions = <T extends {
    id?: number | null;
    isActive?: boolean | null;
    isValid?: boolean | null;
    package?: { package?: unknown } | null;
}>(
    applicableSubscriptions?: ApplicableSubscriptionsReadOnly | null,
    fallbackSubscriptions?: T[] | null,
    listing?: { metaData?: { applicableSubscriptions?: number[] } } | null
): T[] => {
    let userSubs = toSubscriptionArray(
        applicableSubscriptions?.userSubscriptions as T | T[] | undefined
    );

    const applicableIds = listing?.metaData?.applicableSubscriptions ?? [];
    if (applicableIds.length && fallbackSubscriptions?.length) {
        const applicableSet = new Set(applicableIds);
        const existingIds = new Set(
            userSubs
                .map((sub) => sub.id)
                .filter((id): id is number => id != null)
        );
        const extras = fallbackSubscriptions.filter(
            (sub) =>
                sub.id != null &&
                applicableSet.has(sub.id) &&
                !existingIds.has(sub.id)
        );
        if (extras.length) {
            userSubs = [...userSubs, ...extras];
        }
    }

    const withPackage = userSubs.filter((sub) => sub.package?.package);
    const active = withPackage.filter((sub) => sub.isActive && sub.isValid);
    return active.length > 0 ? active : withPackage;
};

export const pickOldestListingUserSubscription = <T = {
    id?: number | null;
    isActive?: boolean | null;
    isValid?: boolean | null;
    createdAt?: string | null;
}>(
    applicableSubscriptions?: ApplicableSubscriptionsReadOnly | null,
    preferredIds?: Array<number | string> | null,
    fallbackSubscriptions?: T[] | null
): T | undefined => {
    let userSubs = toSubscriptionArray(
        applicableSubscriptions?.userSubscriptions as T | T[] | undefined
    );

    if (preferredIds?.length && fallbackSubscriptions?.length) {
        const preferredSet = new Set(
            preferredIds
                .map((id) => Number(id))
                .filter((id) => Number.isFinite(id))
        );
        const hasPreferred = userSubs.some(
            (sub) =>
                (sub as { id?: number | null }).id != null &&
                preferredSet.has(
                    Number((sub as { id?: number | null }).id)
                )
        );
        if (!hasPreferred) {
            const extras = fallbackSubscriptions.filter(
                (sub) =>
                    (sub as { id?: number | null }).id != null &&
                    preferredSet.has(
                        Number((sub as { id?: number | null }).id)
                    )
            );
            if (extras.length) {
                userSubs = [...userSubs, ...extras];
            }
        }
    }

    return pickOldestActiveUserSubscription(
        userSubs as Array<{
            id?: number | null;
            isActive?: boolean | null;
            isValid?: boolean | null;
            createdAt?: string | null;
        }>,
        { preferredIds }
    ) as T | undefined;
};

export const getSubscriptionPromoDetails = (
    applicableSubscriptions?: ApplicableSubscriptionsReadOnly | null,
    listingType?: number,
    preferredIds?: Array<number | string> | null,
    fallbackSubscriptions?: RawRecord[] | null
): SubscriptionPromoDetails => {
    const userSub = pickOldestListingUserSubscription<RawRecord>(
        applicableSubscriptions,
        preferredIds,
        fallbackSubscriptions
    );
    if (!userSub) {
        return { appliesFor: [], cap: null, packageName: null };
    }

    const { appliesFor, cap } = extractPromoFromUserSub(userSub, listingType);
    const packageNode = userSub.package as RawRecord | undefined;

    return {
        appliesFor,
        cap,
        packageName: (packageNode?.name as string) ?? null,
    };
};

export const subscriptionAppliesToUnit = (
    appliesFor: string[],
    unit: string
): boolean => appliesFor.includes(unit);

export const listingEligibleViaMetadata = (
    listing?: { metaData?: { applicableSubscriptions?: number[] } } | null,
    applicableSubscriptions?: ApplicableSubscriptionsReadOnly | null
): boolean => {
    const applicableIds = listing?.metaData?.applicableSubscriptions ?? [];
    if (!applicableIds.length) return false;

    const userSubs = toSubscriptionArray(
        applicableSubscriptions?.userSubscriptions as { id?: number } | { id?: number }[] | undefined
    );
    return userSubs.some(
        (sub) => sub?.id != null && applicableIds.includes(sub.id)
    );
};

/** Legacy ListingLabels / PriceClient: subscription query param is the package name when unit matches. */
export const resolveSubscriptionForUnit = (
    applicableSubscriptions: ApplicableSubscriptionsReadOnly | null | undefined,
    listingType: number | undefined,
    priceUnit: string,
    listing?: { metaData?: { applicableSubscriptions?: number[] } } | null,
    preferredIds?: Array<number | string> | null,
    fallbackSubscriptions?: RawRecord[] | null
): string | null => {
    const { appliesFor, packageName } = getSubscriptionPromoDetails(
        applicableSubscriptions,
        listingType,
        preferredIds,
        fallbackSubscriptions
    );
    if (!packageName || !priceUnit) return null;

    if (subscriptionAppliesToUnit(appliesFor, priceUnit)) {
        return packageName;
    }

    if (
        !appliesFor.length &&
        listingEligibleViaMetadata(listing, applicableSubscriptions)
    ) {
        return packageName;
    }

    return null;
};

export const hasUserApplicableSubscriptions = (
    applicableSubscriptions?: ApplicableSubscriptionsReadOnly | null,
    listing?: { metaData?: { applicableSubscriptions?: number[] } } | null
): boolean => {
    if (toSubscriptionArray(applicableSubscriptions?.applicableSubscriptions).length > 0) {
        return true;
    }
    if (toSubscriptionArray(applicableSubscriptions?.userSubscriptions).length > 0) {
        return true;
    }
    return listingEligibleViaMetadata(listing, applicableSubscriptions);
};

export const PRICE_UNIT_ORDER = ['hour', 'day', 'month', 'annual'] as const;

/**
 * Union of `appliesFor` booking units across the user's active packages
 * for a listing type (desk / meeting room / office), ordered hour → day →
 * month → annual. Normalizes `year` → `annual`.
 */
export const collectAppliesForUnitsFromSubscriptions = (
    subscriptions: RawRecord[] | null | undefined,
    listingType?: number
): string[] => {
    if (!subscriptions?.length) return [];

    const units = new Set<string>();
    for (const sub of subscriptions) {
        const { appliesFor } = extractPromoFromUserSub(sub, listingType);
        for (const unit of appliesFor) {
            const normalized = unit === 'year' ? 'annual' : unit;
            if ((PRICE_UNIT_ORDER as readonly string[]).includes(normalized)) {
                units.add(normalized);
            }
        }
    }

    return PRICE_UNIT_ORDER.filter((unit) => units.has(unit));
};

export const getAvailablePriceUnits = (
    priceObj?: Record<string, number> | null
): string[] =>
    PRICE_UNIT_ORDER.filter((unit) => unit in (priceObj || {}));

/** Pick initial booking unit: URL frequency → subscription appliesFor → fallback order. */
export const resolveInitialPriceUnit = (options: {
    priceObj?: Record<string, number> | null;
    frequencyParam?: string | null;
    preferredUnit?: string | null;
    appliesFor?: string[];
}): string => {
    const available = getAvailablePriceUnits(options.priceObj);
    if (!available.length) return 'hour';

    const { frequencyParam, preferredUnit, appliesFor = [] } = options;

    if (frequencyParam) {
        const preferred = frequencyParam
            .split(',')
            .map((unit) => unit.trim())
            .filter(Boolean)
            .find((unit) => available.includes(unit));
        if (preferred) return preferred;
    }

    const subscriptionUnits = appliesFor.filter((unit) =>
        available.includes(unit)
    );
    if (subscriptionUnits.length > 0) {
        return (
            PRICE_UNIT_ORDER.find((unit) => subscriptionUnits.includes(unit)) ??
            subscriptionUnits[0]
        );
    }

    if (preferredUnit && available.includes(preferredUnit)) {
        return preferredUnit;
    }

    return available[0];
};

type SubscriptionPromoUsage = {
    remainingUsage?: number | null;
};

type SubscriptionPromoUserSub = {
    hotdeskPromo?: SubscriptionPromoUsage;
    meetingRoomPromo?: SubscriptionPromoUsage;
};

/** Remaining membership passes for the listing type on the applicable sub. */
export const getSubscriptionRemainingPasses = (options: {
    applicableSubscriptions?: ApplicableSubscriptionsReadOnly | null;
    listingType?: number;
    preferredIds?: Array<number | string> | null;
    fallbackSubscriptions?: SubscriptionPromoUserSub[] | null;
}): number => {
    const promoType = getListingPromoType(options.listingType);
    if (!promoType) return 0;

    const userSub = pickOldestListingUserSubscription<SubscriptionPromoUserSub>(
        options.applicableSubscriptions,
        options.preferredIds,
        options.fallbackSubscriptions
    );
    if (!userSub) return 0;
    const promo = userSub[promoType];
    return Math.max(0, promo?.remainingUsage ?? 0);
};

/**
 * True when the listing unit price exceeds the membership promo cap for the
 * selected plan — the same signal the details sidebar uses for strikethrough
 * pricing (`price - cap > 0`).
 */
/**
 * Unit price after membership promo cap — matches listing details sidebar
 * (`price - cap` when cap < price, otherwise 0). Never negative.
 */
export const getMembershipDiscountedUnitPrice = (
    unitPrice: number,
    cap: number | null | undefined
): number => {
    if (!Number.isFinite(unitPrice) || unitPrice <= 0) return 0;
    if (cap == null || !Number.isFinite(cap) || cap >= unitPrice) return 0;
    return Math.max(0, unitPrice - cap);
};

export const membershipUnitPriceExceedsCap = (options: {
    listingPrice?: Record<string, number> | null;
    bookingPlan: string;
    applicableSubscriptions?: ApplicableSubscriptionsReadOnly | null;
    listingType?: number;
    preferredIds?: Array<number | string> | null;
    fallbackSubscriptions?: RawRecord[] | null;
}): boolean => {
    const unitPrice = options.listingPrice?.[options.bookingPlan];
    if (unitPrice == null || !Number.isFinite(unitPrice)) return false;

    const { cap, appliesFor } = getSubscriptionPromoDetails(
        options.applicableSubscriptions,
        options.listingType,
        options.preferredIds,
        options.fallbackSubscriptions
    );
    if (cap == null || !Number.isFinite(cap)) return false;
    if (
        appliesFor.length > 0 &&
        !subscriptionAppliesToUnit(appliesFor, options.bookingPlan)
    ) {
        return false;
    }
    return unitPrice > cap;
};

export const estimateBookingPassesNeeded = (
    period: number,
    seats: number
): number => {
    const safePeriod = Math.max(0, period);
    const safeSeats = seats > 0 ? seats : 1;
    return safePeriod * safeSeats;
};

/**
 * Whether a membership booking still requires payment. Uses the quote total
 * when available; otherwise infers from unit price vs promo cap and from
 * estimated pass consumption vs remaining passes (details-page logic).
 */
export const subscriptionBookingNeedsPayment = (options: {
    quoteFinalPrice?: number | null;
    listingPrice?: Record<string, number> | null;
    bookingPlan: string;
    listingType?: number;
    applicableSubscriptions?: ApplicableSubscriptionsReadOnly | null;
    preferredIds?: Array<number | string> | null;
    fallbackSubscriptions?: SubscriptionPromoUserSub[] | null;
    estimatedPeriod?: number;
    estimatedSeats?: number;
}): boolean => {
    const quotePrice = options.quoteFinalPrice;
    if (quotePrice != null && Number.isFinite(quotePrice)) {
        return quotePrice > 0;
    }

    if (
        membershipUnitPriceExceedsCap({
            listingPrice: options.listingPrice,
            bookingPlan: options.bookingPlan,
            applicableSubscriptions: options.applicableSubscriptions,
            listingType: options.listingType,
            preferredIds: options.preferredIds,
            fallbackSubscriptions: options.fallbackSubscriptions as
                | RawRecord[]
                | null,
        })
    ) {
        return true;
    }

    const remaining = getSubscriptionRemainingPasses({
        applicableSubscriptions: options.applicableSubscriptions,
        listingType: options.listingType,
        preferredIds: options.preferredIds,
        fallbackSubscriptions: options.fallbackSubscriptions,
    });
    const passesNeeded = estimateBookingPassesNeeded(
        options.estimatedPeriod ?? 1,
        options.estimatedSeats ?? 1
    );
    return passesNeeded > remaining;
};
